import React, { useEffect, useMemo, useState } from 'react';
import {
    bp_physical_threats, bp_vaccine_initiatives, bp_vaccine_safety_misinfo,
    bp_translate_mention, bp_get_retention_settings, bp_watchlists_get,
} from '../../../services';
import { formatNumber, unwrap } from './_shared';
import { Icon } from './_icons';
import ModuleIcon from '../../../Components/ModuleIcon';
import { platformLabel } from './LanguageAnalysis';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTabs from '../../../Components/CommonTabs';
import NoDataPage from '../../../Components/NoDataPage';
import StatCard from '../../../Components/StatCard';
import StatusBadge from '../../../Components/StatusBadge';
import AddOnCard from '../../AddOns/AddOnCard';
import { lightTheme as T } from '../../../Constants/Colors';
import { SubHeading, Body } from '../../../Constants/Fonts';
import './brand-mentions.scss';

const DAYS_OPTIONS = [
    { value: 7, label: 'Last 7 days' },
    { value: 14, label: 'Last 14 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
];

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];

const SURFACES = [
    {
        key: 'physical', title: 'Physical & activist threats',
        sub: 'Facility, protest, incitement, exec-travel and event targeting',
        findingsOf: d => d.physical?.detailed_findings || [],
    },
    {
        key: 'vaccine', title: 'Initiative impersonation',
        sub: 'Accounts or sites impersonating your initiatives',
        findingsOf: d => d.vaccine?.detailed_findings || [],
    },
    {
        key: 'vacSafety', title: 'Safety misinformation',
        sub: 'Claims scored against the WHO + CDC false-claim corpus',
        findingsOf: d => d.vacSafety?.detailed_findings || [],
    },
];

function SevPill({ severity }) {
    return (
        <StatusBadge status={severity} dot={false} transform="uppercase">
            {severity || '-'}
        </StatusBadge>
    );
}

function NovaChip({ nova }) {
    if (!nova) return null;
    // Only confirmed AI verdicts carry a FORESEE chip. Unconfirmed findings
    // already show a "needs review" badge; surfacing a heuristic confidence
    // here would read as a verdict it isn't.
    if (nova.provider !== 'bedrock_claude') return null;
    const conf = Number(nova.confidence || 0);
    const pct = Math.round(conf * 100);
    const rationale = nova.rationale ? `\n${nova.rationale}` : '';
    const evidence = nova.evidence_quote ? `\nEvidence: “${nova.evidence_quote}”` : '';
    return (
        <StatusBadge variant="purple" dot={false} transform="uppercase"
            title={`FORESEE AI: ${nova.label || 'unclassified'} · confidence ${pct}%${rationale}${evidence}`}>
            FORESEE AI · {pct}%
        </StatusBadge>
    );
}

function LangChip({ lang, translatedFrom }) {
    if (!lang) return null;
    const label = translatedFrom ? `${translatedFrom}→en` : lang;
    return <StatusBadge variant="neutral" dot={false} transform="none">{label}</StatusBadge>;
}

// ISO-3166 → emoji-flag via the unicode regional-indicator trick.
// US → 🇺🇸, GB → 🇬🇧, etc. Returns null for 'XX' or invalid 2-letter codes
// so the chip can be hidden when geo signal is absent.
function flagFromIso(iso) {
    if (!iso || iso.length !== 2 || iso === 'XX') return null;
    const up = iso.toUpperCase();
    if (!/^[A-Z]{2}$/.test(up)) return null;
    return String.fromCodePoint(...[...up].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function CountryChip({ iso, name, signal, count }) {
    const flag = flagFromIso(iso);
    if (!flag) return null;
    const tooltip = signal
        ? `${name || iso}${count ? ` · ${count} mentions` : ''} · inferred via ${signal}`
        : (name || iso);
    return (
        <StatusBadge variant="neutral" dot={false} transform="none" title={tooltip}>
            {flag} {iso}{count ? ` ${count}` : ''}
        </StatusBadge>
    );
}

// Best-practice response guidance per threat category. Mirrors how
// physical-security / threat-intel teams (Recorded Future, ZeroFox,
// corporate GSOC) run a finding: triage → assess → escalate to the
// right owner → respond → document. Guidance is advisory text - it
// doesn't auto-action, because the response owner and the right call
// are human decisions.
const RESPONSE_PLAYBOOK = {
    exec_travel_targeting: {
        owner: 'Executive Protection / Physical Security',
        steps: [
            'Verify the exec’s travel details aren’t publicly exposed (itinerary, hotel, routing).',
            'Brief the protection detail; consider varying routing or timing if the threat is credible.',
            'Cross-check the author against known-hostile actors and prior incidents.',
        ],
    },
    travel_chatter: {
        owner: 'Physical Security (monitor)',
        steps: [
            'Confirm whether the chatter references a real upcoming trip vs. historical/PR mention.',
            'No action if benign context; intensify monitoring if hostile sentiment appears.',
        ],
    },
    facility_targeting: {
        owner: 'Facility Security + Local Law Enforcement liaison',
        steps: [
            'Alert on-site security at the named facility; review access controls and perimeter.',
            'If an organised protest/blockade is indicated, coordinate with local law enforcement.',
            'Stage comms in case of disruption.',
        ],
    },
    protest_mobilisation: {
        owner: 'Security + Comms',
        steps: [
            'Assess scale, date, and location of the organising signal.',
            'Brief comms for a holding statement; adjust facility/event posture if a site is named.',
            'Monitor for escalation toward incitement.',
        ],
    },
    activist_targeting: {
        owner: 'Security + Comms',
        steps: [
            'Identify the campaign/group and its history with the organisation.',
            'Decide engage-vs-monitor with comms; preserve evidence of any threats.',
        ],
    },
    incitement: {
        owner: 'Security + Legal + Law Enforcement (urgent)',
        steps: [
            'Escalate immediately. Do not wait for the scheduled review cycle.',
            'Preserve evidence (screenshots, URLs, author handles, timestamps).',
            'Report to the platform’s threat/abuse channel; involve law enforcement if credible or imminent.',
        ],
    },
    event_targeting: {
        owner: 'Event Security + travelling staff',
        steps: [
            'Coordinate with the event’s security team for the date window.',
            'Brief travelling staff; heighten monitoring before/during the event.',
        ],
    },
    vaccine_initiative_impersonation: {
        owner: 'Legal / Brand + Comms',
        steps: [
            'File a platform takedown for the impersonating account/site.',
            'Issue a public clarification and alert affected partners/donors.',
        ],
    },
    vaccine_safety_misinfo: {
        owner: 'Medical Affairs + Comms',
        steps: [
            'Draft a factual rebuttal grounded in the WHO/CDC corpus.',
            'Report to the platform; track spread and reach before deciding on public response.',
        ],
    },
};

function TextList({ items, ordered }) {
    const Tag = ordered ? 'ol' : 'ul';
    return (
        <Tag className="bm_tm_list">
            {items.map((t, i) => <li key={i}><Body as="span" variant={2} className="bm_tm_text">{t}</Body></li>)}
        </Tag>
    );
}

function ResponsePlaybook({ subcategory, severity }) {
    const pb = RESPONSE_PLAYBOOK[subcategory];
    if (!pb) return null;
    const urgent = severity === 'High' || severity === 'Critical' || subcategory === 'incitement';
    return (
        <div className="bm_tm_playbook">
            <div className="bm_tm_playbook_head">
                <Body as="span" variant={2} weight="bold" className="bm_tm_label">Recommended response</Body>
                {urgent && <StatusBadge variant="danger" dot={false} transform="uppercase">Urgent</StatusBadge>}
                <Body as="span" variant={2} className="bm_tm_muted">Owner: {pb.owner}</Body>
            </div>
            <TextList items={pb.steps} ordered />
        </div>
    );
}

const METHOD_STEPS = [
    { n: 1, t: 'Capture', d: 'Every brand mention the scanner collects across X, Reddit, Facebook, LinkedIn, news, blogs, and forums is fed in. Threat Monitor re-reads that pool, it doesn’t fetch separately.' },
    { n: 2, t: 'Keyword + watchlist baseline', d: 'High-recall first pass: matches threat vocabulary (protest / incitement / travel / activist terms) and your configured facility, event, and exec-travel watchlists.' },
    { n: 3, t: 'FORESEE AI confirmation', d: 'Each keyword hit is sent to FORESEE AI for semantic intent classification, separating a real threat from benign context (e.g. “launched at Davos” vs “targeting the exec at Davos”).' },
    { n: 4, t: 'Corroboration + severity', d: 'A finding is promoted only with hostile corroboration (negative sentiment, risk-tagged author, incitement language, or FORESEE AI confirmation). Severity is assigned, and confirmed High/Critical findings auto-page on-call.' },
];

function Panel({ title, note, aside, children }) {
    return (
        <section className="bm_tm_panel cmn_elevated_card">
            {(title || note || aside) && (
                <div className="bm_tm_panel_head">
                    <div className="bm_tm_panel_heading">
                        {title && <SubHeading as="h3" variant={2} className="bm_tm_panel_title">{title}</SubHeading>}
                        {note && <Body as="span" variant={2} className="bm_tm_muted">{note}</Body>}
                    </div>
                    {aside && <div className="bm_tm_panel_aside">{aside}</div>}
                </div>
            )}
            {children}
        </section>
    );
}

function MethodologyPanel() {
    const [open, setOpen] = useState(false);
    return (
        <Panel
            title="How we identify threats"
            note="4-stage detection pipeline"
            aside={<CommonButton text={open ? 'Hide' : 'Show'} variant="white" onClick={() => setOpen(o => !o)} />}
        >
            {open && (
                <>
                    <div className="bm_tm_steps">
                        {METHOD_STEPS.map(s => (
                            <AddOnCard key={s.n}
                                align="flex-start"
                                iconNode={<Body as="span" weight="bold" className="bm_tm_step_no">{s.n}</Body>}
                                label={(
                                    <>
                                        <Body as="div" weight="bold" className="bm_tm_card_title">{s.t}</Body>
                                        <Body as="div" variant={2} className="bm_tm_card_text">{s.d}</Body>
                                    </>
                                )}
                                action={null}
                            />
                        ))}
                    </div>
                    <Body variant={2} className="bm_tm_muted bm_tm_footnote">
                        Three surfaces are monitored: physical and activist targeting (facility, protest,
                        incitement, exec travel, event), initiative impersonation (against your
                        watchlist) and safety misinformation (scored against the WHO + CDC
                        false-claim corpus).
                    </Body>
                </>
            )}
        </Panel>
    );
}

// Business-risk rationale per subcategory - customer-facing prose explaining
// *why* this kind of finding warrants attention. Pairs with the
// API-driven `i_button` (the detection method) inside <WhyHowPanel>.
const WHY_THIS_IS_A_RISK = {
    incitement: 'Explicit calls for violence, intimidation, or harm directed at your people, partners, or facilities. Even unverified threats create duty-of-care exposure and demand immediate triage to keep staff safe.',
    exec_travel_targeting: 'Posts referencing an exec’s upcoming travel narrow the window of physical access risk. Adversaries use these signals to plan protests, ambushes, or unwanted contact at known waypoints (hotel, route, venue).',
    travel_chatter: 'Leadership-travel chatter (Davos / WEF / WHO summit / etc.) raises ambient exposure during a fixed window. Most is benign news framing; hostile sentiment is the tell that converts chatter into a security signal.',
    facility_targeting: 'Mentions naming a specific facility can indicate reconnaissance, protest planning, or grievance focused on that site. Early visibility lets local security adjust posture before an event materialises.',
    protest_mobilisation: 'Posts organising or mobilising protest activity against your brand. Scale, date, and location signals let security and comms decide whether to engage, monitor, or harden the named site.',
    activist_targeting: 'Coordinated activist campaigns targeting the organisation. Repeat campaigns track over time; a sudden spike usually precedes a public-facing action and warrants comms engagement.',
    event_targeting: 'Posts referencing a watched event within its date window: protest, disruption, or targeting risk. Acting inside the window protects staff and attendees before the event itself is impacted.',
    vaccine_initiative_impersonation: 'Impersonating accounts or look-alike sites tied to your initiatives deceive partners, donors, and the public. Left up, they erode trust and divert traffic from authoritative channels.',
    vaccine_safety_misinfo: 'Posts asserting your products cause harm, are unsafe, or fall under conspiracy narratives. Risk: erosion of public confidence, donor reputation impact, secondary effects on partner uptake. The 24h detection SLA matters here: early intervention limits the audience that ever sees the claim.',
};

// Compute the "how we detected this" bullet points from the finding's evidence.
// All inputs come from the API - counts, samples, FORESEE verdicts - so the
// bullets describe *this specific finding*, not generic copy.
function detectionBullets(f) {
    const ev = f.evidence || {};
    const samples = ev.samples || [];
    const bullets = [];

    if (ev.count > 0) {
        bullets.push(`${formatNumber(ev.count)} mention${ev.count === 1 ? '' : 's'} in the lookback window matched the keyword / pattern baseline.`);
    }
    if (ev.risk_tagged_authors > 0) {
        bullets.push(`${ev.risk_tagged_authors} mention${ev.risk_tagged_authors === 1 ? '' : 's'} came from authors on your risk-tagged source watchlist. That alone clears the hostile-corroboration gate even when sentiment looks benign.`);
    }
    const novaConfirmed = samples.filter(s => s?.nova?.provider === 'bedrock_claude');
    if (novaConfirmed.length > 0) {
        const avg = novaConfirmed.reduce((sum, s) => sum + Number(s.nova.confidence || 0), 0) / novaConfirmed.length;
        const pct = Math.round(avg * 100);
        bullets.push(`FORESEE AI confirmed the threat label on ${novaConfirmed.length} sample${novaConfirmed.length === 1 ? '' : 's'} (avg confidence ${pct}%). This is what promoted the cluster from keyword candidate to a confirmed finding.`);
    }
    const translated = samples.filter(s => s?.nova?.translated_from);
    if (translated.length > 0) {
        bullets.push(`${translated.length} sample${translated.length === 1 ? '' : 's'} ${translated.length === 1 ? 'was' : 'were'} originally non-English; FORESEE translated to English before re-running the pattern match (catches paraphrases the source-language regex would miss).`);
    }
    return bullets;
}

function WhyHowPanel({ f }) {
    // Prefer the per-finding prose the backend now ships (`why_this_is_a_risk`
    // and `how_we_detected_this`) so each card describes its own evidence
    // rather than a generic per-subcategory blurb. Fall back to the local
    // copy for older payloads or subcategories the backend hasn't filled.
    const why = f.why_this_is_a_risk || WHY_THIS_IS_A_RISK[f.subcategory];
    const apiHow = f.how_we_detected_this;
    const how = (apiHow && apiHow.method) || f.i_button;
    const bullets = (apiHow && Array.isArray(apiHow.bullets) && apiHow.bullets.length > 0)
        ? apiHow.bullets
        : detectionBullets(f);
    if (!why && !how && bullets.length === 0) return null;

    return (
        <div className="bm_tm_explain">
            {why && (
                <div className="bm_tm_block">
                    <Body as="div" variant={2} weight="bold" className="bm_tm_label">Why this is a risk</Body>
                    <Body variant={2} className="bm_tm_text">{why}</Body>
                </div>
            )}
            {(how || bullets.length > 0) && (
                <div className="bm_tm_block">
                    <Body as="div" variant={2} weight="bold" className="bm_tm_label">How we detected this</Body>
                    {how && <Body variant={2} className="bm_tm_text is-method">{how}</Body>}
                    {bullets.length > 0 && <TextList items={bullets} />}
                </div>
            )}
        </div>
    );
}

function FindingCard({ f, onDismiss }) {
    const ev = f.evidence || {};
    const samples = ev.samples || [];
    const countries = ev.countries || [];
    const [open, setOpen] = useState(f.severity === 'High' || f.severity === 'Critical');
    const toggle = () => setOpen(o => !o);
    const facts = [
        ev.count != null && { label: 'Mentions', value: formatNumber(ev.count) },
        ev.risk_tagged_authors != null && { label: 'Risk-tagged authors', value: formatNumber(ev.risk_tagged_authors) },
        ev.target && ev.target !== 'general' && { label: 'Target', value: ev.target },
        ev.initiative && { label: 'Initiative', value: ev.initiative },
    ].filter(Boolean);

    return (
        <article className={`bm_tm_finding${open ? ' is-open' : ''}`}>
            <div className="bm_tm_finding_head"
                role="button" tabIndex={0} aria-expanded={open}
                title={open ? 'Collapse' : 'Expand details'}
                onClick={toggle}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}>
                <Icon name="chevron" size={14} className="bm_tm_chevron" />
                <SevPill severity={f.severity} />
                {f.review_status === 'needs_review' && (
                    <StatusBadge variant="warning" dot={false} transform="uppercase"
                        icon={<Icon name="clock" size={11} />}
                        title="Surfaced for analyst review, not yet confirmed or escalated to on-call.">
                        needs review
                    </StatusBadge>
                )}
                {f.review_status === 'confirmed' && (f.severity === 'High' || f.severity === 'Critical') && (
                    <StatusBadge variant="danger" dot={false} transform="uppercase"
                        icon={<Icon name="bell" size={11} />}
                        title="Confirmed by FORESEE AI or a risk-tagged author, and paged to on-call.">
                        paged
                    </StatusBadge>
                )}
                <Body as="span" weight="semibold" className="bm_tm_finding_title">{f.description}</Body>
                {f.subcategory && (
                    <StatusBadge variant="neutral" dot={false} transform="uppercase">
                        {String(f.subcategory).replace(/_/g, ' ')}
                    </StatusBadge>
                )}
                <span className="bm_tm_finding_side">
                    {!open && (
                        <Body as="span" variant={2} className="bm_tm_muted">
                            {formatNumber(ev.count || 0)} mention{ev.count === 1 ? '' : 's'}
                        </Body>
                    )}
                    {onDismiss && (
                        <CommonButton
                            text="Dismiss"
                            variant="white"
                            onClick={e => { e.stopPropagation(); onDismiss(f); }}
                            title="Dismiss: mark this as not a threat for this session"
                        />
                    )}
                </span>
            </div>
            {open && (
                <div className="bm_tm_finding_body">
                    <WhyHowPanel f={f} />
                    {(facts.length > 0 || countries.length > 0) && (
                        <div className="bm_tm_facts">
                            {facts.map(x => (
                                <div key={x.label} className="bm_tm_fact">
                                    <Body as="span" variant={2} weight="bold" className="bm_tm_label">{x.label}</Body>
                                    <Body as="span" weight="semibold" className="bm_tm_fact_value">{x.value}</Body>
                                </div>
                            ))}
                            {countries.length > 0 && (
                                <div className="bm_tm_fact is-wide">
                                    <Body as="span" variant={2} weight="bold" className="bm_tm_label">Geo footprint</Body>
                                    <span className="bm_tm_chips">
                                        {countries.slice(0, 8).map(c => (
                                            <CountryChip key={c.iso} iso={c.iso} name={c.name} count={c.count} />
                                        ))}
                                        {countries.length > 8 && (
                                            <Body as="span" variant={2} className="bm_tm_muted">+{countries.length - 8} more</Body>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    {samples.length > 0 && (
                        <div className="bm_tm_block">
                            <Body as="div" variant={2} weight="bold" className="bm_tm_label">Sample mentions</Body>
                            <div className="bm_tm_samples">
                                {samples.slice(0, 5).map((s, i) => <SampleRow key={s.id || i} s={s} />)}
                            </div>
                        </div>
                    )}
                    {/* Best-practice response guidance for this threat type. */}
                    <ResponsePlaybook subcategory={f.subcategory} severity={f.severity} />
                </div>
            )}
        </article>
    );
}

function SampleRow({ s }) {
    const [translation, setTranslation] = useState('');
    const [translating, setTranslating] = useState(false);
    const [translateError, setTranslateError] = useState('');

    const translate = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (translating || translation) return;
        const id = s.id || s.mention_id;
        if (!id) {
            setTranslateError('Translation not available for this mention.');
            return;
        }
        setTranslating(true);
        setTranslateError('');
        try {
            const res = await bp_translate_mention(id);
            const d = unwrap(res) || {};
            if (d.translated_text) {
                setTranslation(d.translated_text);
            } else {
                setTranslateError(d.error === 'translation_unavailable'
                    ? 'FORESEE AI is currently unavailable.'
                    : 'Translation failed.');
            }
        } catch (err) {
            setTranslateError('Translation failed.');
        } finally {
            setTranslating(false);
        }
    };

    const displayText = translation || s.text_preview || s.text || '(no preview)';

    return (
        <div className="bm_tm_sample">
            <div className="bm_tm_sample_meta">
                <Body as="span" variant={2} weight="semibold" className="bm_tm_sample_handle">{s.handle || '(no handle)'}</Body>
                {s.platform && <Body as="span" variant={2} className="bm_tm_muted">{platformLabel(s.platform)}</Body>}
                <NovaChip nova={s.nova} />
                <LangChip lang={s.nova?.language} translatedFrom={s.nova?.translated_from} />
                <CountryChip iso={s.country_iso} name={s.country_name} signal={s.country_signal} />
            </div>
            <Body variant={2} className="bm_tm_sample_text">{displayText}</Body>
            {translateError && <Body variant={2} className="bm_tm_error">{translateError}</Body>}
            {(s.url || !translation) && (
                <div className="bm_tm_sample_actions">
                    {s.url && (
                        <Body as="a" variant={2} weight="semibold" className="bm_tm_link"
                            href={s.url} target="_blank" rel="noopener noreferrer">
                            Open mention ↗
                        </Body>
                    )}
                    {!translation && (translating
                        ? <CommonLoader size={12} label="Translating…" />
                        : (
                            <Body as="button" type="button" variant={2} weight="semibold" className="bm_tm_link"
                                onClick={translate}>
                                Translate to English
                            </Body>
                        ))}
                </div>
            )}
        </div>
    );
}

const WATCHLIST_SUBJECTS = [
    { field: 'facility_watchlist', one: 'facility', many: 'facilities' },
    { field: 'event_watchlist', one: 'event', many: 'events' },
    { field: 'exec_travel_watchlist', one: 'exec trip', many: 'exec trips' },
    { field: 'vaccine_initiative_watchlist', one: 'initiative', many: 'initiatives' },
];
const countOf = (n, one, many) => `${formatNumber(n)} ${n === 1 ? one : many}`;
const joinAnd = parts => (parts.length > 1
    ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
    : parts[0] || '');

function threatDescription({ watchlists, corpusVersion, windowLabel, loading, totalFindings, sevCounts, needsReview }) {
    const sentences = [];
    if (watchlists) {
        const corpus = `the WHO + CDC corpus${corpusVersion ? ` (${corpusVersion})` : ''}`;
        const watched = WATCHLIST_SUBJECTS
            .map(s => ({ ...s, n: (watchlists[s.field] || []).length }))
            .filter(s => s.n > 0)
            .map(s => countOf(s.n, s.one, s.many));
        sentences.push(watched.length
            ? `Watching ${joinAnd(watched)} from your watchlists, and checking safety claims against ${corpus}.`
            : `No watchlist entries yet (add them in Settings → Watchlists), so only safety claims are checked, against ${corpus}.`);
    }
    if (loading) {
        if (!sentences.length) sentences.push('Loading threat findings…');
    } else if (totalFindings > 0) {
        const urgent = ['Critical', 'High']
            .filter(s => sevCounts[s] > 0)
            .map(s => `${formatNumber(sevCounts[s])} ${s.toLowerCase()}`);
        sentences.push(`${windowLabel}: ${countOf(totalFindings, 'finding', 'findings')}`
            + (urgent.length ? ` (${urgent.join(', ')})` : '')
            + (needsReview > 0 ? `, ${formatNumber(needsReview)} awaiting review` : '')
            + '.');
    } else {
        sentences.push(`${windowLabel}: no threats found.`);
    }
    return sentences.join(' ');
}

const goToWatchlists = () => window.dispatchEvent(
    new CustomEvent('bm-tab-switch', { detail: { tab: 'settings' } }));

export default function ThreatMonitorTab({ onSubtitle }) {
    const [days, setDays] = useState(30);
    const [physical, setPhysical] = useState(null);
    const [vaccine, setVaccine] = useState(null);
    const [vacSafety, setVacSafety] = useState(null);
    const [loading, setLoading] = useState(true);
    // Customizable view - severity filter + session dismissals.
    const [sevFilter, setSevFilter] = useState('all'); // all|Critical|High|Medium|Low
    const [dismissed, setDismissed] = useState(() => new Set());

    const dismissFinding = (f) => {
        // Stable key from description+subcategory+target.
        const key = `${f.description}|${f.subcategory}|${f.evidence?.target || ''}`;
        setDismissed(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    };
    const visibleFilter = (list) => (list || []).filter(f => {
        const key = `${f.description}|${f.subcategory}|${f.evidence?.target || ''}`;
        if (dismissed.has(key)) return false;
        if (sevFilter !== 'all' && f.severity !== sevFilter) return false;
        return true;
    });

    const [retentionDays, setRetentionDays] = useState(null);
    const [watchlists, setWatchlists] = useState(null);

    useEffect(() => {
        let alive = true;
        bp_watchlists_get()
            .then(res => {
                const body = unwrap(res);
                const known = WATCHLIST_SUBJECTS.some(s => Array.isArray(body?.[s.field]));
                if (alive) setWatchlists(known ? body : null);
            })
            .catch(() => { });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        bp_get_retention_settings()
            .then(res => { if (alive) setRetentionDays(unwrap(res)?.retention_days ?? null); })
            .catch(() => { /* retention unknown - leave all ranges available */ });
        return () => { alive = false; };
    }, []);

    // Date-range options can't exceed the org's data-retention window: mentions
    // older than `retentionDays` are purged, so a longer range returns nothing.
    // Hide any option longer than retention (keep-forever = null/0 keeps every
    // range), and clamp the current selection if it's now too long.
    const dayOptions = useMemo(() => {
        if (!retentionDays) return DAYS_OPTIONS;
        const withinRetention = DAYS_OPTIONS.filter(o => o.value <= retentionDays);
        return withinRetention.length ? withinRetention : [DAYS_OPTIONS[0]];
    }, [retentionDays]);

    useEffect(() => {
        if (retentionDays && days > retentionDays) {
            const maxWithin = dayOptions[dayOptions.length - 1];
            if (maxWithin && maxWithin.value !== days) setDays(maxWithin.value);
        }
    }, [retentionDays, days, dayOptions]);

    useEffect(() => {
        setLoading(true);
        Promise.allSettled([
            bp_physical_threats({ days }),
            bp_vaccine_initiatives({ days }),
            bp_vaccine_safety_misinfo({ days }),
        ]).then(([p, v, vs]) => {
            const ok = r => r?.status === 'fulfilled' ? r.value : null;
            setPhysical(unwrap(ok(p)) || {});
            setVaccine(unwrap(ok(v)) || {});
            setVacSafety(unwrap(ok(vs)) || {});
            setLoading(false);
        });
    }, [days]);

    const loaded = { physical, vaccine, vacSafety };
    const surfaces = SURFACES.map(s => ({ ...s, findings: s.findingsOf(loaded) }));
    const allFindings = surfaces.flatMap(s => s.findings);
    const totalFindings = allFindings.length;
    const paging = physical?.paging || null;

    const sevCounts = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    allFindings.forEach(f => {
        const s = f.severity || 'Low';
        if (sevCounts[s] != null) sevCounts[s] += 1;
    });
    const urgentCount = sevCounts.Critical + sevCounts.High;
    // Precision-gate rollup (physical-threat scanner only emits this).
    const review = physical?.review_summary || {};
    const needsReviewCount = review.needs_review || 0;
    const highCritNeedsReview = review.high_crit_needs_review || 0;
    // All three endpoints return the same org-wide all-time mention count.
    // Pick the first one that has it (physical_threats is the most reliable
    // since it's always fetched first).
    const totalAllTime = (
        physical?.total_all_time_mentions
        ?? vaccine?.total_all_time_mentions
        ?? vacSafety?.total_all_time_mentions
        ?? null
    );

    const windowLabel = (dayOptions.find(o => o.value === days) || {}).label || `Last ${days} days`;
    const windowText = windowLabel.toLowerCase();
    const description = threatDescription({
        watchlists, corpusVersion: vacSafety?.corpus_version, windowLabel, loading,
        totalFindings, sevCounts, needsReview: needsReviewCount,
    });
    useEffect(() => { if (onSubtitle) onSubtitle(description); }, [description, onSubtitle]);

    const sevValues = ['all', ...SEVERITIES];

    return (
        <div className="bm_tm">
            <div className="bm_tm_toolbar">
                <div className="bm_tm_toolbar_filters">
                    {!loading && totalFindings > 0 && (
                        <CommonTabs
                            tabs={sevValues.map(s => (s === 'all'
                                ? `All (${totalFindings})`
                                : `${s} (${sevCounts[s]})`))}
                            values={sevValues}
                            active={sevFilter}
                            handlers={sevValues.map(s => () => setSevFilter(s))}
                        />
                    )}
                </div>
                <div className="bm_tm_toolbar_controls">
                    {dismissed.size > 0 && (
                        <CommonButton
                            text={`Restore ${dismissed.size} dismissed`}
                            variant="white"
                            onClick={() => setDismissed(new Set())}
                        />
                    )}
                    <CommonDropdown
                        ariaLabel="Date range"
                        value={days}
                        onChange={setDays}
                        options={dayOptions}
                    />
                </div>
            </div>

            {loading && (
                <Panel>
                    <div className="bm_tm_loading">
                        <CommonLoader label="Loading threat findings…" />
                    </div>
                </Panel>
            )}

            {!loading && totalFindings === 0 && (
                <Panel>
                    {(totalAllTime && totalAllTime > 0) ? (
                        <NoDataPage
                            icon={<Icon name="shield" size={24} color={T.success} />}
                            title="All clear: no threats detected"
                            hint={`Nothing in the ${windowText} matched a watchlist entry or a documented false-claim pattern (${countOf(totalAllTime, 'mention', 'mentions')} captured in total).`}
                        />
                    ) : (
                        <NoDataPage
                            icon={<Icon name="shield" size={24} color={T.primary} />}
                            title="No threat findings yet"
                            hint="Findings appear once mentions match a watchlist entry (facilities, events, exec travel, initiatives) or a documented false-claim pattern."
                            action={<CommonButton text="Go to Settings → Watchlists" variant="primary" onClick={goToWatchlists} />}
                        />
                    )}
                </Panel>
            )}

            {!loading && totalFindings > 0 && (
                <>
                    <div className="bm_tm_kpis">
                        <StatCard className="cmn_elevated_card"
                            label="Findings"
                            value={formatNumber(totalFindings)}
                            hint={`${windowText} · ${SURFACES.length} threat surfaces`}
                            icon={<span className="bm_kpi_icon"><ModuleIcon name="compliance" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Critical + high"
                            value={formatNumber(urgentCount)}
                            valueColor={urgentCount > 0 ? T.danger : undefined}
                            hint={`${sevCounts.Critical} critical · ${sevCounts.High} high`}
                            icon={<span className={`bm_kpi_icon is-${urgentCount > 0 ? 'danger' : 'neutral'}`}><ModuleIcon name="risk" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Awaiting review"
                            value={formatNumber(needsReviewCount)}
                            valueColor={needsReviewCount > 0 ? T.warning : undefined}
                            hint={needsReviewCount > 0
                                ? (highCritNeedsReview > 0
                                    ? `${highCritNeedsReview} high / critical · confirm or dismiss below`
                                    : 'confirm or dismiss below')
                                : 'nothing pending'}
                            title="Surfaced for analyst review. Expand a finding to confirm it or dismiss it; confirmed High / Critical findings are escalated to on-call."
                            icon={<span className={`bm_kpi_icon is-${needsReviewCount > 0 ? 'warning' : 'neutral'}`}><ModuleIcon name="history" size={18} color="currentColor" /></span>} iconLeft />
                        {paging && (
                            <StatCard className="cmn_elevated_card"
                                label="Paged to on-call"
                                value={formatNumber(paging.dispatched || 0)}
                                valueColor={paging.failed > 0 ? T.danger : undefined}
                                hint={paging.failed > 0
                                    ? `${paging.failed} of ${paging.total} failed`
                                    : (paging.dispatched || 0) >= (paging.total || 0)
                                        ? 'all pages sent'
                                        : `${paging.total - (paging.dispatched || 0)} not sent yet`}
                                icon={<span className={`bm_kpi_icon is-${paging.failed > 0 ? 'danger' : 'primary'}`}><ModuleIcon name="users" size={18} color="currentColor" /></span>} iconLeft />
                        )}
                    </div>

                    {surfaces.map(s => {
                        const visible = visibleFilter(s.findings);
                        const corpus = s.key === 'vacSafety' && vacSafety?.corpus_version;
                        return (
                            <Panel key={s.key}
                                title={s.title}
                                note={s.sub}
                                aside={(
                                    <>
                                        {corpus && (
                                            <StatusBadge variant="neutral" dot={false} transform="none">
                                                Corpus {vacSafety.corpus_version}
                                            </StatusBadge>
                                        )}
                                        {s.findings.length > 0 ? (
                                            <StatusBadge variant="warning" dot={false} transform="none">
                                                {countOf(s.findings.length, 'finding', 'findings')}
                                            </StatusBadge>
                                        ) : (
                                            <StatusBadge variant="success" dot={false} transform="none"
                                                icon={<Icon name="check" size={11} />}>
                                                Clear
                                            </StatusBadge>
                                        )}
                                    </>
                                )}
                            >
                                {s.findings.length === 0 ? (
                                    <Body variant={2} className="bm_tm_muted bm_tm_panel_line">
                                        Nothing matched in the {windowText}.
                                    </Body>
                                ) : visible.length === 0 ? (
                                    <Body variant={2} className="bm_tm_muted bm_tm_panel_line">
                                        No findings here match the current filter.
                                    </Body>
                                ) : (
                                    <div className="bm_tm_findings">
                                        {visible.map((f, i) => (
                                            <FindingCard key={s.key + i} f={f} onDismiss={dismissFinding} />
                                        ))}
                                    </div>
                                )}
                            </Panel>
                        );
                    })}
                </>
            )}

            {/* Customer-facing methodology explainer, always available. */}
            {!loading && <MethodologyPanel />}
        </div>
    );
}
